/**
 * Mobile Product Gallery - CSS Scroll-Snap Implementation
 * Native browser scroll-snap handles swipe gesture, momentum, and snap-back.
 * JS adds bookend clones for infinite looping and syncs thumbnail highlight.
 *
 * DOM after init: [clone-last] [slide-0] ... [slide-N-1] [clone-first]
 * scrollLeft positions: 0 = clone-last, 1..N = real slides, N+1 = clone-first
 */

(function() {
  'use strict';

  function initMobileGallery() {
    if (window.innerWidth > 749) return;

    const galleries = document.querySelectorAll('.product-gallery-mobile-grid');

    galleries.forEach(function(gallery) {
      // Skip galleries already initialised (re-entry guard)
      if (gallery.dataset.mobileGalleryInit) return;
      gallery.dataset.mobileGalleryInit = '1';

      const gridContainer = gallery.querySelector('.product-gallery-mobile-grid-container');
      const thumbnailsContainer = gallery.querySelector('.product-gallery-thumbnails-mobile');
      const viewer = gallery.querySelector('.product-gallery-viewer-mobile');

      if (!gridContainer || !thumbnailsContainer || !viewer) return;

      const thumbnails = thumbnailsContainer.querySelectorAll('.product-single__thumbnail');
      const originalSlides = Array.from(viewer.querySelectorAll('.product-single__media-slide'));
      const count = originalSlides.length;

      if (!thumbnails.length || count === 0) return;

      // ── Bookend cloning for infinite scroll ────────────────────────────────
      // Only clone when there is more than one slide.

      let slideOffset = 0; // scrollLeft units to reach real slide 0

      if (count > 1) {
        const cloneFirst = originalSlides[0].cloneNode(true);
        const cloneLast  = originalSlides[count - 1].cloneNode(true);

        cloneFirst.setAttribute('aria-hidden', 'true');
        cloneLast.setAttribute('aria-hidden', 'true');

        // Prepend clone of last, append clone of first
        viewer.insertBefore(cloneLast, viewer.firstChild);
        viewer.appendChild(cloneFirst);

        slideOffset = 1; // real slides start at position 1
      }

      let currentIndex = 0; // always refers to real slide index (0-based)
      let teleporting = false;

      // ── Thumbnail state ────────────────────────────────────────────────────

      function updateThumbnailStates(activeIndex) {
        thumbnails.forEach(function(thumb, idx) {
          const link = thumb.querySelector('.product-single__thumbnail-link');
          thumb.classList.toggle('active', idx === activeIndex);
          thumb.classList.toggle('is-nav-selected', idx === activeIndex);
          thumb.classList.toggle('is-selected', idx === activeIndex);

          if (link) {
            link.style.opacity = idx === activeIndex ? '1' : '0.5';
            link.style.borderColor = idx === activeIndex
              ? 'var(--color-orange, rgb(var(--color-button)))'
              : '#fff';
          }
        });

        // Scroll thumbnail column so active thumb stays visible
        const activeThumb = thumbnails[activeIndex];
        if (activeThumb) {
          const thumbTop    = activeThumb.offsetTop;
          const thumbBottom = thumbTop + activeThumb.offsetHeight;
          const colTop      = thumbnailsContainer.scrollTop;
          const colBottom   = colTop + thumbnailsContainer.clientHeight;

          if (thumbTop < colTop) {
            thumbnailsContainer.scrollTo({ top: thumbTop, behavior: 'smooth' });
          } else if (thumbBottom > colBottom) {
            thumbnailsContainer.scrollTo({ top: thumbBottom - thumbnailsContainer.clientHeight, behavior: 'smooth' });
          }
        }
      }

      // ── Slide switching ────────────────────────────────────────────────────

      function switchToSlide(index) {
        if (index < 0 || index >= count) return;
        currentIndex = index;
        originalSlides.forEach(function(slide, idx) {
          slide.classList.toggle('is-selected', idx === index);
        });
        // +slideOffset to skip past the prepended clone
        viewer.scrollTo({ left: (index + slideOffset) * viewer.clientWidth, behavior: 'smooth' });
        updateThumbnailStates(index);
      }

      // ── Sync from native scroll & handle infinite-loop teleport ────────────
      // Use scrollend (fires exactly once when scroll+snap fully stops) so the
      // teleport happens the instant snapping settles — no artificial delay.
      // Fall back to a debounced scroll listener on older browsers.

      function onScrollSettle() {
        if (teleporting) return;

        const rawIndex = Math.round(viewer.scrollLeft / viewer.clientWidth);

        if (count > 1) {
          // Landed on clone-last (position 0) → teleport to real last slide
          if (rawIndex === 0) {
            teleporting = true;
            viewer.scrollLeft = count * viewer.clientWidth; // instant jump
            currentIndex = count - 1;
            originalSlides.forEach(function(slide, idx) {
              slide.classList.toggle('is-selected', idx === currentIndex);
            });
            updateThumbnailStates(currentIndex);
            setTimeout(function() { teleporting = false; }, 50);
            return;
          }

          // Landed on clone-first (position count+1) → teleport to real first slide
          if (rawIndex === count + 1) {
            teleporting = true;
            viewer.scrollLeft = 1 * viewer.clientWidth; // instant jump
            currentIndex = 0;
            originalSlides.forEach(function(slide, idx) {
              slide.classList.toggle('is-selected', idx === 0);
            });
            updateThumbnailStates(0);
            setTimeout(function() { teleporting = false; }, 50);
            return;
          }
        }

        // Normal slide (rawIndex is within 1..count)
        const newIndex = rawIndex - slideOffset;
        if (newIndex !== currentIndex && newIndex >= 0 && newIndex < count) {
          currentIndex = newIndex;
          originalSlides.forEach(function(slide, idx) {
            slide.classList.toggle('is-selected', idx === newIndex);
          });
          updateThumbnailStates(newIndex);
        }
      }

      if ('onscrollend' in window) {
        viewer.addEventListener('scrollend', onScrollSettle, { passive: true });
      } else {
        let scrollTimer;
        viewer.addEventListener('scroll', function() {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(onScrollSettle, 80);
        }, { passive: true });
      }

      // ── Thumbnail clicks ───────────────────────────────────────────────────

      thumbnails.forEach(function(thumb, index) {
        const link = thumb.querySelector('.product-single__thumbnail-link');
        if (link) {
          link.addEventListener('click', function(e) {
            e.preventDefault();
            switchToSlide(index);
          });
        }
      });

      // ── Tap main image to advance ──────────────────────────────────────────

      viewer.addEventListener('click', function(e) {
        if (e.target.tagName !== 'A' && e.target.tagName !== 'BUTTON' && !e.target.closest('a, button')) {
          switchToSlide((currentIndex + 1) % count);
        }
      });

      // ── Prev / Next arrow buttons ──────────────────────────────────────────

      var prevBtn = gallery.querySelector('.product-gallery-mobile-arrow--prev');
      var nextBtn = gallery.querySelector('.product-gallery-mobile-arrow--next');

      if (prevBtn) {
        prevBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          var w = viewer.clientWidth || viewer.offsetWidth;
          var target = ((currentIndex - 1 + count) % count + slideOffset) * w;
          viewer.scrollTo ? viewer.scrollTo({ left: target, behavior: 'smooth' }) : (viewer.scrollLeft = target);
          currentIndex = (currentIndex - 1 + count) % count;
          updateThumbnailStates(currentIndex);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          var w = viewer.clientWidth || viewer.offsetWidth;
          var target = ((currentIndex + 1) % count + slideOffset) * w;
          viewer.scrollTo ? viewer.scrollTo({ left: target, behavior: 'smooth' }) : (viewer.scrollLeft = target);
          currentIndex = (currentIndex + 1) % count;
          updateThumbnailStates(currentIndex);
        });
      }

      // ── Thumbnail column height ────────────────────────────────────────────

      function updateThumbnailHeight() {
        const height = viewer.clientHeight;
        if (height > 100) {
          thumbnailsContainer.style.height    = height + 'px';
          thumbnailsContainer.style.maxHeight = height + 'px';
          thumbnailsContainer.style.overflowY = 'auto';
          thumbnailsContainer.style.overflowX = 'hidden';
          thumbnailsContainer.style.setProperty('--thumbnail-height', height + 'px');
        }
      }

      let resizeTimer;
      window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateThumbnailHeight, 250);
      });

      // Re-check height as images load (viewer height can change)
      originalSlides.forEach(function(slide) {
        const img = slide.querySelector('img');
        if (img) {
          if (img.complete) {
            updateThumbnailHeight();
          } else {
            img.addEventListener('load', updateThumbnailHeight, { once: true });
          }
        }
      });

      // ── Init ───────────────────────────────────────────────────────────────

      originalSlides[0] && originalSlides[0].classList.add('is-selected');
      updateThumbnailStates(0);
      setTimeout(updateThumbnailHeight, 100);
      setTimeout(updateThumbnailHeight, 500);

      // Set initial scroll position to real slide 0 (skip past the clone-last bookend)
      if (count > 1) {
        requestAnimationFrame(function() {
          viewer.scrollLeft = slideOffset * viewer.clientWidth;
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileGallery);
  } else {
    initMobileGallery();
  }

  document.addEventListener('shopify:section:load', function(event) {
    if (event.detail.sectionId && document.querySelector('.product-gallery-mobile-grid')) {
      setTimeout(initMobileGallery, 100);
    }
  });
})();
